import React, { useState } from 'react';
import axios from 'axios';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Link,
  Avatar,
  Fade,
  Stack,
  Divider
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function Login({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [register, setRegister] = useState(false);
  const [role, setRole] = useState('cashier');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      if (register) {
        await axios.post(`${API_URL}/auth/register`, { username, password, role });
        setSuccess('Registration successful! You can now log in.');
        setRegister(false);
        setUsername('');
        setPassword('');
        setRole('cashier');
      } else {
        const res = await axios.post(`${API_URL}/auth/login`, { username, password });
        onLogin(res.data);
      }
    } catch (err) {
      setError(err.response?.data?.error || (register ? 'Registration failed' : 'Login failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Fade in timeout={600}>
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Card sx={{ maxWidth: 420, width: '100%', p: 3, boxShadow: 8, borderRadius: 5 }}>
          <CardContent>
            <Stack alignItems="center" spacing={2} mb={2}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 64, height: 64 }}>
                <LockOutlinedIcon fontSize="large" />
              </Avatar>
              <Typography variant="h5" fontWeight={700} color="primary.main">
                {register ? 'Create Account' : 'Sign In'}
              </Typography>
            </Stack>
            <Box component="form" onSubmit={handleSubmit} autoComplete="off" sx={{ mt: 1 }}>
              <TextField
                label="Username"
                value={username}
                onChange={e => setUsername(e.target.value)}
                required
                fullWidth
                margin="normal"
                autoFocus
                InputProps={{ sx: { borderRadius: 3 } }}
              />
              <TextField
                label="Password"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                fullWidth
                margin="normal"
                InputProps={{ sx: { borderRadius: 3 } }}
              />
              {register && (
                <FormControl fullWidth margin="normal">
                  <InputLabel id="role-label">Role</InputLabel>
                  <Select
                    labelId="role-label"
                    value={role}
                    label="Role"
                    onChange={e => setRole(e.target.value)}
                    sx={{ borderRadius: 3 }}
                  >
                    <MenuItem value="cashier">Cashier</MenuItem>
                    <MenuItem value="admin">Admin</MenuItem>
                  </Select>
                </FormControl>
              )}
              {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
              {success && <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>}
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                sx={{ mt: 3, mb: 1, py: 1.7, fontWeight: 700, fontSize: 18, borderRadius: 3, boxShadow: 2, letterSpacing: 0.5 }}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
              >
                {register ? 'Register' : 'Login'}
              </Button>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Box textAlign="center" mt={2}>
              {register ? (
                <Link component="button" variant="body2" underline="hover" color="primary" fontWeight={600} onClick={() => { setRegister(false); setError(''); setSuccess(''); }}>
                  Already have an account? <b>Sign In</b>
                </Link>
              ) : (
                <Link component="button" variant="body2" underline="hover" color="primary" fontWeight={600} onClick={() => { setRegister(true); setError(''); setSuccess(''); }}>
                  New user? <b>Create Account</b>
                </Link>
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Fade>
  );
}

export default Login;
